import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect, useDispatch } from 'react-redux'
import dayjs from 'dayjs'

import Actions from '../../../../../actions'
import Operations from '../../../../../operations'
import Selectors from '../../../../../selectors'
import { EMARPaywall } from '../components'
import { EMARResidentPDF } from './components'
import { EMARResidentMARChartScreen } from './screens'

import { withRouter } from '../../../../../withRouter'
import useQuery from '../../../../../hooks/useQuery'
import useFeatureFlags from 'hooks/useFeatureFlags'

/**
@description Renders the EMAR Screen for a resident

@param {array} chartData. The data to be rendered in the chart. Is a collection
of medication objects.

@param {function} fetchData. Fetches data from Redux.

@param {function} fetchLogs. Fetches logs from Redux.

@param {function} openModal. Opens the Add Medication modal.

@param {object} params. The parameters for the screen. Contains the resident id.

@param {object} resident. Object containing the resident's data, e.g. their
name.

@returns {function} ResidentEMARScreen.
*/

export function EMARResidentScreen(props) {
  const {
    chartData,
    chartDataUpdated,
    fetchData,
    router: {
      params: { id },
    },
    resident,
    home,
  } = props

  // PDF export works by rendering the screen with search params
  // Eg. /residents/602fc3830ba1fe8d6e4a7473/pdf/emar?printpdf=1&startDate=...
  const query = useQuery()
  const isPDF = query.get('printpdf')
  const startDateQuery = query.get('startDate')
  const endDateQuery = query.get('endDate')

  const [startDate, setStartDate] = React.useState(
    isPDF && startDateQuery
      ? dayjs(startDateQuery)
      : dayjs().subtract(14, 'days')
  )
  const [endDate, setEndDate] = React.useState(
    isPDF && endDateQuery ? dayjs(endDateQuery) : dayjs()
  )

  const stateProps = { endDate, startDate, setEndDate, setStartDate }
  const dispatch = useDispatch()
  const bugfixEmarFlag = useFeatureFlags({
    flagNames: ['bugfix_emar_su_update'],
  })?.data?.bugfix_emar_su_update?.enabled

  useEffect(() => {
    if (id) {
      dispatch(Actions.setLoadedList('emar-round-timings', null))
      dispatch(Actions.setLoadedList('emar-rounds', null))
      dispatch(Actions.setLoadedList('emar-resident-medications', null))
      fetchData(startDate, endDate, id)
    }
  }, [startDate, endDate, id])

  if (!resident) return null
  if (!bugfixEmarFlag && !chartData) return null

  if (!home?.features?.eMAR?.enabled) return <EMARPaywall />

  const residentName = `${resident.name.first} ${resident.name.last}`
  const mainMarkup = isPDF ? (
    <EMARResidentPDF
      {...props}
      endDate={endDate}
      residentName={residentName}
      startDate={startDate}
    />
  ) : (
    <EMARResidentMARChartScreen
      {...props}
      residentName={residentName}
      chartData={bugfixEmarFlag ? chartDataUpdated : chartData}
      {...stateProps}
    />
  )

  return mainMarkup
}

EMARResidentScreen.propTypes = {
  chartData: PropTypes.array,
  chartDataUpdated: PropTypes.array,
  fetchData: PropTypes.func.isRequired,
  fetchLogs: PropTypes.func.isRequired,
  home: PropTypes.object.isRequired,
  openModal: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  resident: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
}

const mapStateToProps = (state, props) => ({
  chartData: Selectors.getResidentMarChart(state, props),
  chartDataUpdated: Selectors.getResidentMarChartUpdated(state, props),
  resident: Selectors.getResidentProfile(state, {
    id: props.router.params.id,
  }),
  user: Selectors.getCurrentUserProfile(state),
  home: state.global.home,
  location: window.location,
})

const mapDispatchToProps = (dispatch) => ({
  fetchData: (startDate, endDate, resident) => {
    dispatch(Operations.loadList('carers'))
    dispatch(Operations.loadList('emar-round-timings'))
    dispatch(
      Operations.loadList('emar-rounds', {
        resident,
        from: dayjs(startDate).startOf('day').toISOString(),
        to: dayjs(endDate).endOf('day').toISOString(),
      }),
      false
    )
    dispatch(
      Operations.loadList('emar-resident-medications', { resident }, false)
    )
  },
  openModal: (resident) => {
    dispatch(Operations.openModal({ modalId: 'addEmarMedication' }))
    dispatch(
      Actions.addForm({
        formId: 'addEmarMedication',
        initial: { resident },
      })
    )
  },
  openOnboardingModal: () => {
    dispatch(Operations.openModal({ modalId: 'onboardingEmar' }))
    dispatch(
      Actions.addForm({
        formId: 'onboardingEmar',
      })
    )
  },
  openBilling: () => {
    dispatch(
      Operations.openNewPlan({
        planId: 'e-mar',
        redirectUrl: '?',
      })
    )
  },
})

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(EMARResidentScreen)
)
